// W42 W4/F16 — the wishes peak card.
//
// `/wishes` is one of the four F5 情緒型 routes and it was the only one with **no
// saturated card at all**: the whole screen was a white list, which is why §2's
// audit found `surface-*` at 0–1 components per route and why the reference
// comparison read as a different product rather than a different skin.
//
// ### What goes in it, and what deliberately does not
//
// The obvious candidate was the progress/budget summary, and it is the wrong
// answer — §8.3's ruling. A completion percentage is a **datum**: it is read,
// compared across sessions, and acted on. spec §3.1's test is 「换掉颜色用户会
// 读错信息吗」, and a saturated block behind a number is a number the user is
// invited to feel rather than to read. So the progress/budget blocks stay white
// or glass, and the peak card carries the one thing on this screen that is not
// a number: **which wish is closest**.
//
// `days_until` is still a number, and that is fine, because it is a *countdown* —
// the same role the anniversaries peak card plays with 「下一個」, and the same
// reason F13 reserves `coral` for numeral-only cards.

import { Link } from "react-router-dom";

import { HeroCard } from "@/components/primitives/HeroCard";
import { HERO_WISHES } from "@/lib/screenType";
import type { WishSummary } from "@/lib/api/wishlist";

/**
 * The soonest wish with a target date still in front of us.
 *
 * Local rather than exported: `react-refresh/only-export-components` warns on a
 * non-component export from a component file (eslint.config.js:57), and a helper
 * this small does not earn a module of its own.
 */
function nearestWish(wishes: readonly WishSummary[]): WishSummary | null {
  let best: WishSummary | null = null;
  let bestDays = Number.POSITIVE_INFINITY;
  for (const w of wishes) {
    // Same reason as the grouping in `Wishes.tsx`: `items: [null]` is a 200
    // response the API layer passes through unvalidated, and `w.days_until` on
    // it threw `Cannot read properties of null` — so one malformed row took the
    // peak card down even after the board itself had been taught to skip it.
    // A row that is not an object cannot be a wish; it is not a candidate.
    if (!w || typeof w !== "object") continue;
    const days = w.days_until;
    if (days === null || days < 0) continue; // undated, or already past
    if (w.status === "abandoned") continue;
    if (days < bestDays) {
      best = w;
      bestDays = days;
    }
  }
  return best;
}

const PRIORITY_LABEL: Record<WishSummary["priority"], string> = {
  must: "一定要做到",
  want: "想做到",
  maybe: "隨緣",
};

function countdown(days: number): string {
  if (days === 0) return "就是今天";
  if (days === 1) return "明天";
  return `還有 ${days} 天`;
}

export interface WishPeakCardProps {
  wishes: readonly WishSummary[];
}

export function WishPeakCard({ wishes }: WishPeakCardProps): JSX.Element | null {
  // With no dated wish there is nothing to put on a saturated block, and F16's
  // 「每屏恰好 1 塊」 must not become 「每屏最多 1 塊」 — a second card would
  // break the peak the first one exists to create.
  const wish = nearestWish(wishes);
  if (!wish) return null;

  return (
    <HeroCard slot={HERO_WISHES} testId="wishes-peak-card" className="mt-s-4">
      <Link
        to={`/wishes/${wish.id}`}
        data-testid="wishes-peak-link"
        className="press block pr-16 transition-opacity hover:opacity-90"
      >
        {/* `pr-16` clears the 56px mascot plus its 14px inset. Without it the
            longest wish names run underneath the character, which is the one
            place on the card where text and decoration collide. */}
        <p className="text-meta font-semibold uppercase tracking-wider text-ink-1/70">
          {wish.days_until === 0 ? "今天" : "最近的一個心願"}
        </p>
        <p className="mt-s-1 truncate font-display text-title-2 text-ink-1">
          {wish.name}
        </p>
        <p className="mt-s-2 text-body-sm text-ink-1/80">
          {countdown(wish.days_until ?? 0)} · {PRIORITY_LABEL[wish.priority]} · 共 {wishes.length} 個
        </p>
      </Link>
    </HeroCard>
  );
}
